/**
 * This plugin handles the client side of CloudFront routing.
 *
 * By CloudFront routing I mean the trick/hack where we rewrite the URI of the CloudFront
 * request event inside a request (Lambda or edge) function in order to force S3 to serve
 * a different object other than what the URI would suggest.
 *
 * So normal course is that browser requests /path/to/page and there is an object in S3
 * at /path/to/page which has been generated by Nuxt using the WordPress page data of
 * a page of slug `page` and parent `to` and grandparent `path`. This is the normal
 * course for most pages.
 *
 * But some pages are special. For various reasons we are not able to match up the browser
 * request URI with the S3 object / WordPress page. The solution is to rewrite the request
 * URI in the origin_request / viewer_request lambda/edge function. This forces S3 to return
 * the correct object. Step 1 of 2 done.
 *
 * Step 2 of 2 is that the browser parses the page returned and the page hydrates. Now Nuxt
 * takes over and has to be satisfied. Nuxt will see the canonical URL created during
 * build time and want to reroute to that path. In this plugin, we prevent this from
 * taking place. Thus completes the illusion!
 */

const dynamicPaths = [
  "/home-generic-variant",
  "/home",
  "/home-b",
  "/home-c",
  "/man-and-van-b",
  "/courier-services/furniture-couriers-b",
  "/removals/home-removals-b",
  "/removals/home-removals-c",
  "/es/inicio",
  "/fr/accueil",
  "/local/postcode",
  "/es/zona/postcode",
  "/fr/region/postcode",
  "/us/home"
]
const pathsUsedInAbTests = [
  "/av/low-cost-removal-services-*",
  "/av/low-cost-couriers",
  "/av/low-cost-removal-experts-london",
  "/low-cost-removal-services",
  "/av/low-cost-removal-experts",
  "/av/man-and-van-about-quotes",
  "/partners/affiliates-instant-pricing",
  "/removals/home-removals",
  "/"
]

function matchPathWithWildcard(pattern: string, path: string) {
  /**
   * Note: only works if wildcard is at the end of the pattern
   */
  if (!pattern.endsWith("*")) {
    return pattern === path
  }
  const base = pattern.slice(0, -1)
  return path.startsWith(base)
}

const stripTrailingSlash = (str: string) => {
  if (str.endsWith("/")) {
    return str.slice(0, str.length - 1)
  }
  return str
}

const pageUnderAbTest = (currentPath: string, pathsUsedInAbTests: string[]): boolean => {
  for (let i = 0; i < pathsUsedInAbTests.length; i++) {
    const desiredPath = pathsUsedInAbTests[i]
    if (currentPath === desiredPath || matchPathWithWildcard(desiredPath, currentPath)) {
      return true
    }
  }
  return false
}

const pathRewrittenByCdn = (currentPath: string, redirectToPath: string): boolean =>
  redirectToPath !== currentPath && dynamicPaths.includes(redirectToPath)

export default defineNuxtPlugin(nuxtApp => {
  if (import.meta.client) {
    const router = useRouter()

    router.beforeEach((to, from, next) => {
      console.log("[Nuxt 3 Router] Navigating from", from.path, "to", to.path)

      const currentPath = stripTrailingSlash(from.path)
      const redirectToPath = to.path

      if (
        pathRewrittenByCdn(currentPath, redirectToPath) ||
        pageUnderAbTest(currentPath, pathsUsedInAbTests)
      ) {
        console.log("Blocked navigation to", redirectToPath)
        return next(false)
      }

      next()
    })
  }
})
